import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { BookOpenIcon, MailIcon, PencilIcon } from 'lucide-react'
import type { EditorPageType, EditorView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { OrderError, TabGate, useOrder } from '@/components/app/order'
import { FormError, LoadingState, errorMessage } from '@/components/app/states'
import { Button, buttonVariants } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { usePublicConfig } from '@/components/marketing/config'

// "Edit your site": opens the full-screen page editor (/o/$orderId/edit, components/app/editor),
// with the HO-2 guide.

export const Route = createFileRoute('/o/$orderId/editor')({
  loader: ({ params, abortController }) => api<EditorView>(`/orders/${encodeURIComponent(params.orderId)}/editor`, { signal: abortController.signal }),
  pendingComponent: () => <LoadingState label="Loading" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/editor">
      <OrderError error={error} title="We could not load this page" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/editor">
      <EditSite />
    </TabGate>
  ),
})

function EditSite() {
  const view = Route.useLoaderData()
  const order = useOrder()
  const supportEmail = usePublicConfig().config?.supportEmail ?? null

  if (view.status === 'ready') return <EditorLauncher view={view} />

  return (
    <section aria-labelledby="edit-site" className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 id="edit-site" className="text-xl font-semibold">
          Edit your site
        </h2>
        <p className="text-sm text-muted-foreground">Change the text and images of your pages, then publish. Visitors see your changes once you publish them.</p>
      </div>

      {view.status === 'unavailable' && (
        <p className="rounded-xl border border-border p-4 text-sm">
          The editor for {order.domain} is not set up yet.{' '}
          {supportEmail ? (
            <>
              Email us at{' '}
              <a href={`mailto:${supportEmail}?subject=${encodeURIComponent(`Editor for ${order.domain}`)}`} className="underline underline-offset-4">
                {supportEmail}
              </a>{' '}
              and we will make changes for you in the meantime.
            </>
          ) : (
            'Email us and we will make changes for you in the meantime.'
          )}
        </p>
      )}
      {view.status === 'pending' && (
        <p className="rounded-xl border border-border p-4 text-sm">
          We are setting up the editor on {order.domain}. We email you the link to the editor as soon as it is ready, usually within a few minutes.
        </p>
      )}
      {view.status !== 'unavailable' && <EditingGuide pages={view.pages} />}
    </section>
  )
}

function EditorLauncher({ view }: { view: EditorView }) {
  const order = useOrder()
  return (
    <section aria-labelledby="edit-site" className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-xl border border-border p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="edit-site" className="text-xl font-semibold">
            Edit your site
          </h2>
          <p className="text-sm text-muted-foreground">
            Change texts, buttons and images on the page, arrange its sections and edit the menu. Changes stay a draft until you publish them.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Link to="/o/$orderId/edit" params={{ orderId: order.id }} className={buttonVariants({ size: 'lg' })}>
            <PencilIcon aria-hidden />
            Open the editor
          </Link>
          <Link to="/o/$orderId/admin" params={{ orderId: order.id }} className={buttonVariants({ size: 'lg', variant: 'outline' })}>
            Open Admin
          </Link>
        </div>
      </div>

      <SignInLink email={view.email} />
      <EditingGuide pages={view.pages} />
    </section>
  )
}

function SignInLink({ email }: { email: string | null }) {
  const { id } = useOrder()
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)

  const send = async () => {
    setState('sending')
    setError(null)
    try {
      await api<{ sent: true }>(`/orders/${encodeURIComponent(id)}/editor/sign-in-link`, { method: 'POST' })
      setState('sent')
    } catch (e) {
      setError(errorMessage(e))
      setState('idle')
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Button variant="outline" className="w-fit" onClick={send} disabled={state === 'sending'}>
        {state === 'sending' ? <Spinner /> : <MailIcon aria-hidden />}
        Email me a new sign-in link
      </Button>
      <p role="status" className="text-sm text-muted-foreground">
        {state === 'sent' ? `Sent${email ? ` to ${email}` : ''}. The link is for you only.` : ''}
      </p>
      <FormError message={error} />
    </div>
  )
}

const WHERE: Record<EditorPageType['where'], string> = {
  pages: 'Pages',
  posts: 'Articles',
  auto: 'Updates by itself',
}

function EditingGuide({ pages }: { pages: EditorPageType[] }) {
  const hasPosts = pages.some((p) => p.where === 'posts')
  return (
    <div className="flex flex-col gap-4 border-t border-border pt-6">
      <h3 className="flex items-center gap-2 font-medium">
        <BookOpenIcon className="size-4" aria-hidden />
        Editing guide
      </h3>

      {pages.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm">Where to find each part of your site in the editor:</p>
          <table className="w-full text-left text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1 pr-4 font-normal">Page type</th>
                <th className="py-1 pr-4 font-normal">Pages</th>
                <th className="py-1 font-normal">In the editor</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.name} className="border-t border-border">
                  <td className="py-1.5 pr-4">{p.name}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{p.count}</td>
                  <td className="py-1.5">{WHERE[p.where]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm text-muted-foreground">Lists of articles, such as category and archive pages, update by themselves when you add or edit an article.</p>
        </div>
      )}

      <dl className="flex flex-col gap-3 text-sm">
        <div>
          <dt className="font-medium">Change text on a page</dt>
          <dd className="text-muted-foreground">Open the editor and click the text. Type your change. The change saves as a draft when you click somewhere else.</dd>
        </div>
        <div>
          <dt className="font-medium">Replace an image</dt>
          <dd className="text-muted-foreground">Click the image and choose a new one. Write a short description. People who cannot see the image hear this description, and search engines use it.</dd>
        </div>
        <div>
          <dt className="font-medium">Change a link or button</dt>
          <dd className="text-muted-foreground">Click the link or button, then change its text or the page it opens.</dd>
        </div>
        <div>
          <dt className="font-medium">Add, move or remove a section</dt>
          <dd className="text-muted-foreground">Open the <b>Sections</b> tab in the side panel.</dd>
        </div>
        <div>
          <dt className="font-medium">Change the menu</dt>
          <dd className="text-muted-foreground">Open the <b>Menu</b> tab. Add, rename or move menu links there.</dd>
        </div>
        <div>
          <dt className="font-medium">Change a page title for search engines</dt>
          <dd className="text-muted-foreground">Open the <b>SEO</b> tab.</dd>
        </div>
        <div>
          <dt className="font-medium">Add, hide, delete or restore a page</dt>
          <dd className="text-muted-foreground">Open the <b>Page</b> tab. Earlier versions of the page are there too.</dd>
        </div>
        {hasPosts && (
          <div>
            <dt className="font-medium">Write an article</dt>
            <dd className="text-muted-foreground">Open <b>Articles</b> in your account and add an article. Give it a title, a short excerpt and an image, then write the text.</dd>
          </div>
        )}
        <div>
          <dt className="font-medium">Publish</dt>
          <dd className="text-muted-foreground">Drafts stay private. <b>Publish</b> puts your changes on your site, and visitors see them within a minute.</dd>
        </div>
      </dl>
    </div>
  )
}
